import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Store, Phone, Mail, MapPin, FileText, Eye, EyeOff, Save, Bell } from "lucide-react";
import type { MarketplaceProfile } from "@shared/schema";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

type FormState = {
  displayName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  stateCode: string;
  bio: string;
  showPhone: boolean;
  showEmail: boolean;
  showAddress: boolean;
};

const DEFAULTS: FormState = {
  displayName: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  stateCode: "",
  bio: "",
  showPhone: false,
  showEmail: false,
  showAddress: true,
};

function profileToForm(p: MarketplaceProfile | null): FormState {
  if (!p) return DEFAULTS;
  return {
    displayName: p.displayName ?? "",
    phone: p.phone ?? "",
    email: p.email ?? "",
    address: p.address ?? "",
    city: p.city ?? "",
    stateCode: p.stateCode ?? "",
    bio: p.bio ?? "",
    showPhone: p.showPhone,
    showEmail: p.showEmail,
    showAddress: p.showAddress,
  };
}

export default function MarketplaceSettingsPage() {
  const { t } = useTranslation(["marketplace", "common"]);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(DEFAULTS);

  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });

  const { data: profile, isLoading } = useQuery<MarketplaceProfile | null>({
    queryKey: ["/api/marketplace/settings/profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/settings/profile");
      return res.json();
    },
  });

  useEffect(() => {
    if (profile !== undefined) {
      setForm(profileToForm(profile));
    }
  }, [profile]);

  const notificationPrefMutation = useMutation({
    mutationFn: (prefs: { marketplaceInquiryEmails: boolean }) =>
      apiRequest("PATCH", "/api/account/notification-preferences", prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:settingsToast.prefFailed"), description: err.message, variant: "destructive" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await apiRequest("PUT", "/api/marketplace/settings/profile", {
        displayName: data.displayName.trim() || null,
        phone: data.phone.trim() || null,
        email: data.email.trim() || null,
        address: data.address.trim() || null,
        city: data.city.trim() || null,
        stateCode: data.stateCode || null,
        bio: data.bio.trim() || null,
        showPhone: data.showPhone,
        showEmail: data.showEmail,
        showAddress: data.showAddress,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || t("marketplace:settingsToast.saveFailed"));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/settings/profile"] });
      toast({ title: t("marketplace:settingsToast.profileSaved"), description: t("marketplace:settingsToast.profileSavedDesc") });
    },
    onError: (err: Error) => {
      toast({ title: t("marketplace:settingsToast.saveFailed"), description: err.message, variant: "destructive" });
    },
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="sm"
          data-testid="button-back-marketplace-settings"
          className="-ml-2"
          onClick={() => navigate("/marketplace")}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">{t("marketplace:settings.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("marketplace:settings.subtitle")}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {/* ── Identity ── */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Store className="w-4 h-4 text-violet-500" />
              <h2 className="font-medium text-sm">{t("marketplace:settings.displayName")}</h2>
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">{t("marketplace:settings.businessName")}</Label>
              <Input
                id="displayName"
                data-testid="input-marketplace-display-name"
                placeholder={t("marketplace:settings.businessNamePlaceholder")}
                value={form.displayName}
                onChange={(e) => set("displayName", e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                {t("marketplace:settings.displayNameHint")}
              </p>
            </div>
          </section>

          <Separator />

          {/* ── Bio ── */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-violet-500" />
              <h2 className="font-medium text-sm">{t("marketplace:settings.about")}</h2>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio">{t("marketplace:settings.shortBio")}</Label>
              <Textarea
                id="bio"
                data-testid="input-marketplace-bio"
                placeholder={t("marketplace:settings.bioPlaceholder")}
                value={form.bio}
                onChange={(e) => set("bio", e.target.value)}
                maxLength={500}
                className="min-h-[80px]"
              />
              <p className="text-xs text-muted-foreground text-right">
                {form.bio.length}/500
              </p>
            </div>
          </section>

          <Separator />

          {/* ── Location ── */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-violet-500" />
              <h2 className="font-medium text-sm">{t("marketplace:settings.location")}</h2>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">{t("marketplace:settings.streetAddress")}</Label>
                <Input
                  id="address"
                  data-testid="input-marketplace-address"
                  placeholder={t("marketplace:settings.addressPlaceholder")}
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  maxLength={200}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">{t("marketplace:settings.city")}</Label>
                  <Input
                    id="city"
                    data-testid="input-marketplace-city"
                    placeholder={t("marketplace:settings.cityPlaceholder")}
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stateCode">{t("marketplace:settings.state")}</Label>
                  <select
                    id="stateCode"
                    data-testid="select-marketplace-state"
                    value={form.stateCode}
                    onChange={(e) => set("stateCode", e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">{t("marketplace:settings.selectState")}</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  {form.showAddress ? (
                    <Eye className="w-4 h-4 text-violet-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{t("marketplace:settings.showAddress")}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("marketplace:settings.showAddressHint")}
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="switch-show-address"
                  checked={form.showAddress}
                  onCheckedChange={(v) => set("showAddress", v)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Contact ── */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-4 h-4 text-violet-500" />
              <h2 className="font-medium text-sm">{t("marketplace:settings.contact")}</h2>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{t("marketplace:settings.phoneNumber")}</Label>
                <div className="flex gap-3">
                  <Input
                    id="phone"
                    data-testid="input-marketplace-phone"
                    type="tel"
                    placeholder={t("marketplace:settings.phonePlaceholder")}
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    maxLength={20}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  {form.showPhone ? (
                    <Eye className="w-4 h-4 text-violet-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{t("marketplace:settings.showPhone")}</p>
                    <p className="text-xs text-muted-foreground">{t("marketplace:settings.showPhoneHint")}</p>
                  </div>
                </div>
                <Switch
                  data-testid="switch-show-phone"
                  checked={form.showPhone}
                  onCheckedChange={(v) => set("showPhone", v)}
                />
              </div>

              <div className="space-y-2 mt-2">
                <Label htmlFor="email">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {t("marketplace:settings.email")}
                  </span>
                </Label>
                <Input
                  id="email"
                  data-testid="input-marketplace-email"
                  type="email"
                  placeholder={t("marketplace:settings.emailPlaceholder")}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  maxLength={200}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  {form.showEmail ? (
                    <Eye className="w-4 h-4 text-violet-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{t("marketplace:settings.showEmail")}</p>
                    <p className="text-xs text-muted-foreground">{t("marketplace:settings.showEmailHint")}</p>
                  </div>
                </div>
                <Switch
                  data-testid="switch-show-email"
                  checked={form.showEmail}
                  onCheckedChange={(v) => set("showEmail", v)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Notifications ── */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-4 h-4 text-violet-500" />
              <h2 className="font-medium text-sm">{t("marketplace:settings.notificationPreferences")}</h2>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium">{t("marketplace:settings.inquiryEmails")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("marketplace:settings.inquiryEmailsHint")}
                </p>
              </div>
              <Switch
                data-testid="switch-marketplace-inquiry-emails"
                checked={user?.marketplaceInquiryEmails !== false}
                onCheckedChange={(checked) =>
                  notificationPrefMutation.mutate({ marketplaceInquiryEmails: checked })
                }
                disabled={notificationPrefMutation.isPending}
              />
            </div>
          </section>

          <Separator />

          {/* Save */}
          <div className="flex justify-end pb-4">
            <Button
              data-testid="button-save-marketplace-profile"
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending}
              className="bg-violet-500 hover:bg-violet-600 text-white min-w-[120px]"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? t("marketplace:settings.saving") : t("marketplace:settings.saveProfile")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
